// Navbar.js
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { UserAuth } from "../context/AuthContext";
import { FaSearch } from "react-icons/fa";
import axios from "axios";
import PurchaseModal from "./PurchaseModal";
import { usePurchasedPrices } from "../context/PurchasedPricesContext";

const Navbar = () => {
  const { user, logOut } = UserAuth();
  const { purchasedPrices, loading } = usePurchasedPrices();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedMuni, setSelectedMuni] = useState(null);

  const handleLogout = async () => {
    try {
      await logOut();
      navigate("/");
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setSearchResults([]);
      return;
    }

    const fetchSearchResults = async () => {
      try {
        const response = await axios.get(process.env.REACT_APP_MUNI_JSON_URL);
        const series = response.data.series;
        const filteredResults = series.filter((item) =>
          item.title.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setSearchResults(filteredResults);
      } catch (error) {
        console.error("Error fetching search results:", error);
      }
    };

    fetchSearchResults();
  }, [searchTerm]);

  const openPurchaseModal = (muni) => {
    setSelectedMuni(muni);
    setModalOpen(true);
  };

  const closePurchaseModal = () => {
    setModalOpen(false);
    setSelectedMuni(null);
  };

  const handleResultClick = (id) => {
    navigate(`/fc/${id}`);
    setSearchTerm("");
    setSearchResults([]);
    setSearchOpen(false);
  };

  if (loading) {
    return <div>Loading...</div>; // Optionally add a loading state
  }

  return (
    <>
      <div className="flex items-center justify-between p-4 z-[100] w-full absolute">
        <Link to="/">
          <img
            className="w-20 h-20 object-cover"
            src={`/ofc_logo.png?${new Date().getTime()}`}
            alt="OFC Logo"
            onError={(e) => {
              console.error("Failed to load image:", e.target.src);
              e.target.src = "/path/to/placeholder.png";
            }}
          />
        </Link>
        <div className="flex items-center">
          <div className="relative flex items-center">
            {!searchOpen ? (
              <FaSearch
                className="text-white cursor-pointer"
                onClick={() => setSearchOpen(true)}
              />
            ) : (
              <div className="relative flex items-center">
                <FaSearch
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 cursor-pointer"
                  onClick={() => {
                    setSearchOpen(false);
                    setSearchTerm("");
                    setSearchResults([]);
                  }}
                />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="p-2 w-48 sm:w-64 border border-gray-300 rounded pl-10"
                  placeholder="Search..."
                />
                {searchResults.length > 0 && (
                  <ul className="bg-white border border-gray-300 rounded-b max-h-64 overflow-y-auto absolute top-12 left-0 w-48 sm:w-64">
                    {searchResults.map((result) => {
                      const isPurchased = result.prodId
                        ? purchasedPrices.includes(result.prodId)
                        : true;
                      return (
                        <li
                          key={result.id}
                          className={`p-2 cursor-pointer hover:bg-gray-200 ${
                            isPurchased ? "" : "bg-gray-300 cursor-not-allowed"
                          }`}
                          onClick={() => {
                            if (isPurchased) {
                              handleResultClick(result.id);
                            } else {
                              if (user) {
                                openPurchaseModal(result);
                              } else {
                                alert("Please log in to purchase");
                              }
                            }
                          }}
                        >
                          {result.title}
                          {!isPurchased && (
                            <button className="bg-red-600 rounded text-xs font-bold text-center px-2 py-1 ml-2">
                              Purchase Now
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </div>
          {user?.email ? (
            <div className="text-right ml-2 sm:ml-4">
              <Link to="/account">
                <button className="text-white py-2 pr-2 sm:pr-4">
                  Account
                </button>
              </Link>
              <button
                onClick={handleLogout}
                className="bg-black px-4 sm:px-6 py-2 rounded cursor-pointer text-white"
              >
                Logout
              </button>
            </div>
          ) : (
            <div className="text-right ml-2 sm:ml-4">
              <Link to="/login">
                <button className="text-white pr-2 sm:pr-4">Sign In</button>
              </Link>
              <Link to="/signup">
                <button className="bg-black px-4 sm:px-6 py-2 rounded cursor-pointer text-white">
                  Sign Up
                </button>
              </Link>
            </div>
          )}
        </div>
      </div>
      <PurchaseModal
        isOpen={modalOpen}
        onClose={closePurchaseModal}
        muni={selectedMuni}
      />
    </>
  );
};

export default Navbar;
